// projectUrls.js — the workspace-scoped canvas-project URL form
// (2026-08-06 per Nika: "for canvas projects
// https://dev.my.symbols.app/w/org/workspace/p/project").
//
//   /w/<org>/p/<key>        → project <org>/<key>, org HQ workspace scope
//   /w/<org>/<ws>/p/<key>   → project <org>/<key>, scoped to workspace <ws>
//   /<owner>/<key>          → legacy/universal form — valid forever (external
//                             links, cross-org projects, signed-out visitors)
//
// The literal `p` segment is a reserved route marker: it can never be a
// workspace handle (static RESERVED_WS_SEG2 gate in ./workspacePrefix.js) and
// never a minted workspace slug (server RESERVED_WORKSPACE_SLUGS,
// src/core/utils/workspaceSlug.js) — so the parse is POSITIONAL and needs no
// workspace data: `p` at segment 3 marks the HQ form, `p` at segment 4 marks
// the workspace form. That keeps cold deep-links parseable before the org's
// workspace list has landed (the shell's handle resolver registers late).
//
// PURE path-string helpers (workspacePrefix.js / guestRoutes.js pattern):
// plain shared/functions module consumed by direct ESM import, NOT via
// el.call. Corrected 2026-08-12 (frank-trap sweep, tickets/sonnet.md) — the
// "NOT registered in functions/index.js — never frank-serialized" claim was
// stale: it IS barrelled (`export * from './projectUrls.js'`), same
// stale-comment class the ticket already flagged for workspacePrefix.js.
// Reads of state/paths are the caller's job (Rule 64: navigation itself still
// goes through el.router).

import { withWorkspacePath } from './workspacePrefix.js'
import { resolveWorkspaceUrlHandle } from './workspaceUrlSync.js'
import { isMwtEnabled } from './mwtFlag.js'

// Parse a `/w/…` pathname into its canvas-project identity, or null when the
// path is not a workspace-scoped project URL (intranet routes, bare `/w/<org>`,
// non-/w/ paths). `owner` is the URL's org segment — the same string that
// would appear as `/<owner>/` in the legacy form, fed straight to the project
// loader. Tail segments after the key are ignored (same permissiveness as the
// legacy `/<owner>/<key>/deep` handling). Query/hash tolerated.
export const parseWorkspaceProjectPath = (pathname) => {
  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer, pure literal — see longPress.js for the identical shape). Org
  // slugs per MODEL: [a-z0-9-]{1,48}. Workspace handles are slugs OR keys
  // (`ws_jfTumkSWof`) or raw ids — same charset as WORKSPACE_TWO_SEG_RE.
  const ORG_SEG_RE = /^[a-z0-9-]{1,48}$/
  const WS_SEG_RE = /^[A-Za-z0-9_-]{1,64}$/

  if (typeof pathname !== 'string' || !pathname) return null
  const segs = pathname.split(/[?#]/)[0].split('/').filter(Boolean)
  if (segs[0] !== 'w') return null
  const owner = segs[1]
  if (!owner || !ORG_SEG_RE.test(owner)) return null
  // HQ form: /w/<org>/p/<key>. Checked FIRST — `p` can never be a workspace
  // handle, so segment 3 being `p` is unambiguous (even for an org whose slug
  // is literally `p`: /w/p/p/<key> parses as org `p`, HQ form).
  if (segs[2] === 'p') {
    return segs[3] ? { owner, wsHandle: null, key: segs[3] } : null
  }
  // Workspace form: /w/<org>/<ws>/p/<key>.
  if (segs[3] === 'p' && segs[4] && WS_SEG_RE.test(segs[2])) {
    return { owner, wsHandle: segs[2], key: segs[4] }
  }
  return null
}

// The href a project link should carry, given where the session is:
//
//   inside the project owner's org (root.org matches `owner` by slug or
//   username, multi-workspace tabs on) → the workspace-scoped form
//   `/w/<org>[/<ws>]/p/<key>`, with the workspace segment following the ONE
//   shared stamping rule (resolveWorkspaceUrlHandle — non-HQ always carries
//   its handle, HQ never does);
//
//   anywhere else (cross-org project, org not resolved yet, MWT off) → the
//   legacy universal `/<owner>/<key>`.
//
// `root` is the root STATE (s.root / el.getRoot().state — the shape holding
// org / workspace / workspaces). Returns null when owner or key is missing so
// callers can guard exactly like buildProjectUrls().
export const projectHref = (root, owner, key) => {
  const o = String(owner || '').trim()
  const k = String(key || '').trim()
  if (!o || !k) return null
  const legacy = `/${o}/${k}`
  if (!isMwtEnabled()) return legacy
  const org = root && root.org
  const orgSlug = (org && (org.slug || org.username)) || null
  if (!orgSlug) return legacy
  const want = o.toLowerCase()
  const inOwnerOrg = [org.slug, org.username]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase() === want)
  if (!inOwnerOrg) return legacy
  const handle = resolveWorkspaceUrlHandle({
    workspace: root.workspace,
    org,
    workspaces: Array.isArray(root.workspaces) ? root.workspaces : []
  })
  // Emit the org's canonical URL slug (not the raw owner string): boot's
  // cross-org reconcile resolves the prefix against org slugs, and the
  // project loader resolves the same slug server-side.
  return withWorkspacePath(orgSlug, handle, `/p/${k}`)
}
